import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, RotateCcw } from "lucide-react";
import { RocSignalLabel } from "@/components/roc/RocSignalLabel";
import { RevealBlock } from "@/components/motion/primitives";
import { cn } from "@/lib/utils";
import { esRoutes, enRoutes } from "@/utils/routes";

interface RocFrameworkExperienceProps {
  language: 'es' | 'en';
}

export interface FrameworkPhase {
  number: string;
  title: string;
  question: string;
  description: string;
  decisions: string[];
}

export const frameworkPhases: Record<'es' | 'en', FrameworkPhase[]> = {
  es: [
    {
      number: "01",
      title: "Investigación",
      question: "¿Cuál es el problema real y qué evidencia lo respalda?",
      description:
        "Se documenta el estado del negocio, la demanda de búsqueda, la competencia real y los datos disponibles antes de proponer cualquier acción.",
      decisions: ["Demanda y competencia del mercado", "Datos disponibles (Search Console, analítica, rastreo)", "Hipótesis inicial del problema"],
    },
    {
      number: "02",
      title: "Base técnica",
      question: "¿Puede el buscador rastrear, renderizar e indexar lo que ya existe?",
      description:
        "Rastreo, indexación, renderizado y arquitectura. Si el sistema no puede leer el sitio, ningún trabajo posterior de contenido tiene un efecto limpio.",
      decisions: ["Rastreo, indexación y renderizado", "Arquitectura de URLs y enlazado interno", "Señales estructuradas y estado del sitio"],
    },
    {
      number: "03",
      title: "Intención de búsqueda",
      question: "¿Qué busca la audiencia y qué página debería responder?",
      description:
        "Mapeo entre intención y plantilla: qué consulta corresponde a categoría, producto, servicio o contenido editorial, y qué huecos existen.",
      decisions: ["Mapa intención ↔ página", "Huecos de cobertura", "Canibalización entre URLs"],
    },
    {
      number: "04",
      title: "Autoridad",
      question: "¿Por qué debería el sistema confiar en este sitio frente a otros?",
      description:
        "Señales de entidad, profundidad temática, evidencia verificable y referencias externas. Sin paquetes de enlaces ni tácticas de riesgo.",
      decisions: ["Entidad y consistencia de marca", "Profundidad temática", "Referencias externas legítimas"],
    },
    {
      number: "05",
      title: "Visibilidad en búsqueda",
      question: "¿Aparece el sitio donde se toma la decisión?",
      description:
        "Cobertura en buscadores tradicionales, resultados locales cuando aplica y presencia en sistemas de búsqueda generativa, sin fórmulas garantizadas.",
      decisions: ["Cobertura orgánica priorizada", "Superficies locales cuando aplica", "Legibilidad para búsqueda con IA"],
    },
    {
      number: "06",
      title: "Conversión",
      question: "¿La visibilidad se traduce en una acción de negocio?",
      description:
        "Coherencia entre la promesa de la consulta y la página de destino: claridad, prueba disponible y una siguiente acción evidente.",
      decisions: ["Coherencia consulta ↔ página", "Claridad de la siguiente acción", "Fricción del formulario o del contacto"],
    },
    {
      number: "07",
      title: "Medición",
      question: "¿Tuvo el cambio el efecto esperado sobre la línea base?",
      description:
        "Cada lote de trabajo se contrasta con la línea base del propio sitio. Lo que no se puede medir se marca como pendiente, no como éxito.",
      decisions: ["Línea base específica del sitio", "Qué se repite, ajusta o descarta", "Nueva hipótesis para la siguiente iteración"],
    },
  ],
  en: [
    {
      number: "01",
      title: "Research",
      question: "What is the real problem, and what evidence supports it?",
      description:
        "The business context, search demand, actual competitors and available data are documented before any action is proposed.",
      decisions: ["Market demand and competition", "Available data (Search Console, analytics, crawl)", "Initial problem hypothesis"],
    },
    {
      number: "02",
      title: "Technical foundation",
      question: "Can the search system crawl, render and index what already exists?",
      description:
        "Crawling, indexing, rendering and architecture. If the system can't read the site, no later content work produces a clean effect.",
      decisions: ["Crawling, indexing and rendering", "URL architecture and internal linking", "Structured signals and site health"],
    },
    {
      number: "03",
      title: "Search intent",
      question: "What is the audience looking for, and which page should answer?",
      description:
        "Mapping intent to template: which query belongs to a category, product, service or editorial page — and where the gaps are.",
      decisions: ["Intent ↔ page map", "Coverage gaps", "Cannibalization between URLs"],
    },
    {
      number: "04",
      title: "Authority",
      question: "Why should the system trust this site over another?",
      description:
        "Entity signals, topical depth, verifiable evidence and external references. No link packages, no risky tactics.",
      decisions: ["Entity and brand consistency", "Topical depth", "Legitimate external references"],
    },
    {
      number: "05",
      title: "Search visibility",
      question: "Does the site show up where the decision is made?",
      description:
        "Coverage across traditional search, local surfaces where relevant, and generative search systems — with no guaranteed formulas.",
      decisions: ["Prioritized organic coverage", "Local surfaces where relevant", "Readability for AI search"],
    },
    {
      number: "06",
      title: "Conversion",
      question: "Does visibility turn into a business action?",
      description:
        "Alignment between what the query promises and what the landing page delivers: clarity, available proof and an obvious next step.",
      decisions: ["Query ↔ page alignment", "Clarity of the next step", "Form and contact friction"],
    },
    {
      number: "07",
      title: "Measurement",
      question: "Did the change have the expected effect on the baseline?",
      description:
        "Every batch of work is compared against the site's own baseline. What can't be measured is flagged as pending, not claimed as a win.",
      decisions: ["Site-specific baseline", "What to repeat, adjust or drop", "New hypothesis for the next iteration"],
    },
  ],
};

/**
 * ROC SEARCH GROWTH FRAMEWORK — única versión vigente (7 fases, ciclo continuo).
 * Sin promesas temporales, de ROI ni de resultados. Interfaz de raíl + fase activa.
 */
export const RocFrameworkExperience = ({ language }: RocFrameworkExperienceProps) => {
  const [active, setActive] = useState(0);
  const routes = language === 'es' ? esRoutes : enRoutes;
  const phases = frameworkPhases[language];
  const current = phases[active];
  const progress = active / (phases.length - 1);

  const t = language === 'es'
    ? {
        eyebrow: "Marco de trabajo",
        title: "ROC Search Growth Framework",
        lead:
          "Siete fases para decidir qué se hace primero en búsqueda, por qué y cómo se comprueba. No es una promesa de plazos ni de resultados: es el orden en el que se toman las decisiones.",
        railLabel: "Fases",
        questionLabel: "Pregunta de la fase",
        decisionsLabel: "Qué se decide",
        loopTitle: "Medición retroalimenta Investigación",
        loopText:
          "La fase 07 no cierra el proceso: devuelve evidencia nueva a la fase 01. Cada iteración empieza con más datos que la anterior.",
        conceptual: "Modelo conceptual",
        cta: "Ver los servicios donde se aplica",
      }
    : {
        eyebrow: "Framework",
        title: "ROC Search Growth Framework",
        lead:
          "Seven phases to decide what gets done first in search, why, and how it gets verified. It isn't a promise of timelines or outcomes — it's the order in which decisions get made.",
        railLabel: "Phases",
        questionLabel: "Phase question",
        decisionsLabel: "What gets decided",
        loopTitle: "Measurement feeds Research",
        loopText:
          "Phase 07 doesn't close the process: it returns new evidence to phase 01. Every iteration starts with more data than the one before.",
        conceptual: "Conceptual model",
        cta: "See the services where it applies",
      };

  return (
    <section className="border-b border-slate-800 bg-slate-950 py-16 lg:py-24">
      <div className="container-custom">
        <RocSignalLabel index="01" tone="invert">{t.eyebrow}</RocSignalLabel>
        <h1 className="font-display mt-6 max-w-3xl text-3xl font-bold leading-[1.1] tracking-tight text-white lg:text-5xl">
          {t.title}
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 lg:text-lg">{t.lead}</p>

        <div className="mt-14 grid gap-10 lg:grid-cols-[1fr_1fr] lg:gap-16">
          {/* Rail */}
          <div className="relative min-w-0">
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">{t.railLabel}</p>
            <span className="absolute left-[7px] top-[46px] h-[calc(100%-60px)] w-px bg-slate-800" aria-hidden="true" />
            <span
              className="absolute left-[7px] top-[46px] w-px bg-brand transition-[height] duration-500 ease-out"
              style={{ height: `calc(${progress * 100}% - ${progress * 60}px)` }}
              aria-hidden="true"
            />
            <ol>
              {phases.map((phase, index) => {
                const isActive = index === active;
                return (
                  <li key={phase.number} className="relative pl-8">
                    <span
                      aria-hidden="true"
                      className={cn(
                        "absolute left-1 top-[20px] h-[7px] w-[7px] transition-colors duration-300",
                        isActive ? "bg-brand" : "bg-slate-700",
                      )}
                    />
                    <button
                      type="button"
                      aria-current={isActive}
                      onClick={() => setActive(index)}
                      onFocus={() => setActive(index)}
                      className="w-full border-b border-slate-800/80 py-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                    >
                      <span className="flex items-baseline gap-4">
                        <span className={cn("font-mono text-[11px]", isActive ? "text-brand" : "text-slate-500")}>
                          {phase.number}
                        </span>
                        <span className={cn("text-base font-semibold", isActive ? "text-white" : "text-slate-400")}>
                          {phase.title}
                        </span>
                      </span>
                      <span className="mt-2 block pl-[38px] text-sm leading-relaxed text-slate-500 lg:hidden">
                        {phase.description}
                      </span>
                      <span className="mt-3 block pl-[38px] lg:hidden">
                        <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
                          {t.decisionsLabel}
                        </span>
                        <span className="mt-2 block space-y-1">
                          {phase.decisions.map((d) => (
                            <span key={d} className="block text-xs leading-relaxed text-slate-500">— {d}</span>
                          ))}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>

            <div className="mt-8 flex items-start gap-3 border border-slate-800 p-5">
              <RotateCcw className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold text-white">{t.loopTitle}</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{t.loopText}</p>
              </div>
            </div>
          </div>

          {/* Active phase */}
          <RevealBlock direction="left" className="hidden min-w-0 lg:block">
            <div className="sticky top-28 border border-slate-800 p-8">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand">
                {language === 'es' ? 'Fase' : 'Phase'} {current.number}
              </p>
              <h2 className="font-display mt-4 text-2xl font-semibold text-white">{current.title}</h2>
              <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">{t.questionLabel}</p>
              <p className="mt-2 text-base leading-relaxed text-slate-200">{current.question}</p>
              <p className="mt-5 text-sm leading-relaxed text-slate-400">{current.description}</p>

              <p className="mt-7 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">{t.decisionsLabel}</p>
              <ul className="mt-3 space-y-2 border-t border-slate-800 pt-4">
                {current.decisions.map((d) => (
                  <li key={d} className="flex gap-3 text-sm text-slate-400">
                    <span className="mt-2 h-px w-4 flex-shrink-0 bg-brand" aria-hidden="true" />
                    {d}
                  </li>
                ))}
              </ul>

              {/* Ciclo conceptual */}
              <svg viewBox="0 0 320 46" className="mt-8 w-full" aria-hidden="true">
                {phases.map((_, i) => {
                  const x = 8 + (i * 304) / (phases.length - 1);
                  return (
                    <g key={i}>
                      {i < phases.length - 1 && (
                        <line
                          x1={x}
                          y1={16}
                          x2={8 + ((i + 1) * 304) / (phases.length - 1)}
                          y2={16}
                          className={i < active ? "stroke-brand" : "stroke-slate-700"}
                          strokeWidth={1}
                        />
                      )}
                      <rect
                        x={x - 4}
                        y={12}
                        width={8}
                        height={8}
                        className={i <= active ? "fill-brand stroke-brand" : "fill-transparent stroke-slate-600"}
                        strokeWidth={1}
                      />
                    </g>
                  );
                })}
                <path d="M312 22 L312 38 L8 38 L8 24" className="stroke-slate-700" strokeWidth={1} fill="none" strokeDasharray="3 3" />
              </svg>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">{t.conceptual}</p>
            </div>
          </RevealBlock>
        </div>

        <div className="mt-12">
          <Link
            to={routes.services}
            className="roc-link inline-flex items-center gap-2 text-sm font-semibold text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            {t.cta}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
};
